import { Avatar, AvatarFallback, AvatarImage } from "@gigpro/ui/Avatar";
import { Button } from "@gigpro/ui/Button";
import { Collapsible, CollapsibleContent } from "@gigpro/ui/Collapsible";
import { ComboboxItem, ComboboxSubItem, Command } from "@gigpro/ui/Combobox";
import { Check, Restore, Settings } from "@gigpro/ui/Icons";
import { Input } from "@gigpro/ui/Input";
import { Separator } from "@gigpro/ui/Separator";
import { Skeleton } from "@gigpro/ui/Skeleton";
import { Tooltip } from "@gigpro/ui/Tooltip";
import type { LocationDropDownContext, LocationManagerRoleEnum, UserResponse } from "@sidegigllc/api/client";
import type { Organization } from "api/resources/Organization";
import { makeIdToCloudinaryUrl } from "utils/cloudinary";

type MinimalLocation = LocationDropDownContext;

type OrganizationComboboxProps = {
  user: UserResponse;
  onLogout: () => void;

  activeOrganization: Organization | undefined;
  organizations: Array<Organization> | undefined;
  onSelectOrganization: (organization: Organization) => void;

  activeLocation: MinimalLocation | undefined;
  viewingLocationName?: string;
  locations: Array<MinimalLocation> | undefined;
  hiddenLocations: Array<MinimalLocation> | undefined;
  onSelectLocation: (location: MinimalLocation) => void;
  onManageLocations: () => void;
};

const roleLabelLookup: Record<LocationManagerRoleEnum, string> = {
  ADMIN: "Admin",
  TEAM_MEMBER: "Team Member",
  ONSITE_MANAGER: "Onsite Manager",
};

export function OrganizationCombobox({
  user,
  onLogout,
  activeLocation,
  viewingLocationName,
  locations,
  hiddenLocations,
  onSelectLocation,
  onManageLocations,
  activeOrganization,
  organizations,
  onSelectOrganization,
}: Readonly<OrganizationComboboxProps>) {
  const hasLocations = locations && locations.length > 0;
  const hasOrganizations = organizations && organizations.length > 0;
  const userRole = user.managed_locations.find(({ location_id }) => location_id === activeLocation?.id)?.role;

  return (
    <div className="flex flex-col rounded-sm bg-white py-2" data-testid="organization-nav-modal">
      <div className="flex flex-col rounded-sm px-3">
        <Tooltip open={user.is_gigpro_admin ? undefined : false} content={`User ID: ${user.id}`}>
          <p className="flex items-center gap-1 text-large font-semibold">
            {user.first_name} {user.last_name}
            {user.is_gigpro_admin && <span className="text-small font-regular text-gray">(Qwick Admin)</span>}
          </p>
        </Tooltip>
        {!!userRole && <p className="text-normal text-gray">{roleLabelLookup[userRole]}</p>}
        <p className="text-normal text-gray">
          {activeLocation && activeOrganization ? (
            <>
              <span className="font-semibold">{activeOrganization.name}</span>
              {activeLocation.name === activeOrganization.name ? "" : `- ${activeLocation.name}`}
            </>
          ) : (
            <Skeleton variant="text" className="w-32" />
          )}
        </p>
        {viewingLocationName && activeLocation && (
          <div className="mt-1 flex items-center gap-2">
            <Tooltip content="You're temporarily viewing a different location from a deep link. Your saved location is unchanged.">
              <p className="truncate text-small text-gray">Currently viewing: {viewingLocationName}</p>
            </Tooltip>
            <Button
              variant="text"
              size="small"
              icon
              className="ml-auto shrink-0 py-0"
              onClick={() => onSelectLocation(activeLocation)}
              aria-label={`Reset to ${activeLocation.name}`}
            >
              <Restore className="size-3.5" />
            </Button>
          </div>
        )}
      </div>
      <Separator className="my-2 bg-light-gray" />
      <Command className="flex flex-col gap-1 transition-all duration-300">
        <Collapsible open={hasLocations || hasOrganizations}>
          <CollapsibleContent className="px-3 pb-1">
            <Command.Input
              data-testid="organization-nav-search"
              placeholder="Search for a location or organization"
              asChild
            >
              <Input inputClassName="py-1 text-normal" />
            </Command.Input>
          </CollapsibleContent>
        </Collapsible>

        <Command.List className="h-(--cmdk-list-height) transition-all duration-300">
          <Command.Group
            className="max-h-40 overflow-y-auto px-3 text-normal"
            value="Locations"
            data-testid="nav-location-section"
          >
            <div className="sticky top-0 z-10 flex items-center bg-white pb-1">
              <p className="font-semibold">Locations</p>
              <Button onClick={onManageLocations} icon className="ml-auto py-0">
                <Settings className="size-4" />
              </Button>
            </div>
            {locations?.length === 0 && <p className="h-7 px-1 py-1.5 text-normal text-gray">No locations available</p>}
            {!locations && (
              <div className="flex gap-1.5 py-1.5 pl-1">
                <Skeleton variant="circle" className="size-4" />
                <Skeleton className="w-32" />
              </div>
            )}
            {locations?.map((location) => (
              <OrgComboboxEntry
                key={location.id}
                type="location"
                id={location.id}
                name={location.name}
                imageUrl={makeIdToCloudinaryUrl(location.image_public_id)}
                selected={location.id === activeLocation?.id}
                onSelect={() => onSelectLocation(location)}
                data-testid={`location-${location.id}`}
              />
            ))}
            {hiddenLocations?.map((location) => (
              <OrgComboboxEntry
                key={location.id}
                type="location"
                id={location.id}
                name={location.name}
                imageUrl={makeIdToCloudinaryUrl(location.image_public_id)}
                hidden
                selected={location.id === activeLocation?.id}
                onSelect={() => onSelectLocation(location)}
                data-testid={`location-${location.id}`}
              />
            ))}
          </Command.Group>

          {hasOrganizations && (
            <>
              <Separator className="mt-1 mb-2 bg-light-gray" />
              <Command.Group
                className="max-h-26 overflow-y-auto px-3 text-normal"
                value="Organizations"
                data-testid="nav-organization-section"
              >
                <p className="sticky top-0 z-10 bg-white pb-1 font-semibold">Organizations</p>
                {organizations?.map((organization) => (
                  <OrgComboboxEntry
                    key={organization.id}
                    type="organization"
                    id={organization.id}
                    name={organization.name}
                    imageUrl={makeIdToCloudinaryUrl(organization.image_public_id)}
                    selected={organization.id === activeOrganization?.id}
                    onSelect={() => onSelectOrganization(organization)}
                    data-testid={`organization-${organization.name}`}
                  />
                ))}
              </Command.Group>
            </>
          )}
        </Command.List>
      </Command>
      <Button onClick={() => onLogout()} variant="contained" size="small" className="mt-3 mr-2 -mb-1 self-end px-4">
        Logout
      </Button>
    </div>
  );
}

interface OrgComboboxEntryProps extends Omit<React.ComponentProps<typeof ComboboxItem>, "id"> {
  type: "location" | "organization";
  id: string | number;
  name: string;
  imageUrl: string | undefined;
  selected: boolean;
  onSelect: () => void;
  hidden?: boolean;
}

function OrgComboboxEntry({
  type,
  id,
  name,
  imageUrl,
  selected,
  onSelect,
  hidden,
  ...props
}: Readonly<OrgComboboxEntryProps>) {
  const Node = hidden ? ComboboxSubItem : ComboboxItem;

  return (
    <Node
      {...props}
      value={`${name} - ${id}`}
      onSelect={onSelect}
      className="flex w-full gap-1.5 rounded-sm py-1 pr-2 pl-1 text-start text-normal transition-colors duration-300 hover:bg-background"
    >
      <Avatar className="size-4">
        <AvatarImage src={imageUrl} alt={name} />
        <AvatarFallback>{name}</AvatarFallback>
      </Avatar>
      {name}
      {selected && <Check className="ml-auto size-3.5 text-success" />}
    </Node>
  );
}
